/**
 * Variant deltas — the config overlays a variant applies to its treatment
 * runs (spec: plans/2.0.0/comparisons/09-variants.md §3).
 *
 * A variant bundles one or more deltas. At dispatch the scheduler resolves
 * them onto each work item:
 *   blocked_partners → SnapshotJob.blocked_url_patterns (see block-partners.ts)
 *   extra_headers    → merged over the job's headers (variant wins on conflict)
 *   url_query        → appended to each page URL via applyUrlQuery()
 * Cookies are phase b (spec Q1) — Lighthouse has no cookie setting, so they
 * need a CDP session in the runner.
 */
import { z } from 'zod';
import { type BlockSelection, resolveBlockedUrlPatterns } from './block-partners.js';

export interface VariantDeltas {
  blocked_partners?: BlockSelection;
  extra_headers?: Record<string, string>;
  url_query?: string;
}

/**
 * Bounded ingress validator for `url_query` — raw query params appended to
 * each page URL ("v=2&flag=on"). No leading `?` (we add it), no fragments.
 */
export const UrlQuerySchema = z
  .string()
  .min(1)
  .max(512)
  .refine((v) => !v.startsWith('?'), { message: 'omit the leading "?"' })
  .refine((v) => !v.includes('#'), { message: 'fragments are not allowed' });

/** Append query params to a URL, preserving any existing query string. */
export function applyUrlQuery(url: string, query: string): string {
  const u = new URL(url);
  // forEach (not for-of): shared's tsconfig lib has no iterable URLSearchParams.
  new URLSearchParams(query).forEach((value, key) => {
    u.searchParams.append(key, value);
  });
  return u.toString();
}

export interface ResolvedVariantDeltas {
  blockedUrlPatterns: string[];
  extraHeaders: Record<string, string>;
  urlQuery?: string;
  /** False when no delta produces a change — dispatching would just re-run baseline. */
  hasWork: boolean;
}

/** Resolve stored deltas into the dispatch-ready shape the scheduler applies per item. */
export function resolveVariantDeltas(deltas: VariantDeltas): ResolvedVariantDeltas {
  const blockedUrlPatterns = deltas.blocked_partners
    ? resolveBlockedUrlPatterns(deltas.blocked_partners)
    : [];
  const extraHeaders = deltas.extra_headers ?? {};
  const urlQuery = deltas.url_query?.trim() || undefined;
  return {
    blockedUrlPatterns,
    extraHeaders,
    urlQuery,
    hasWork:
      blockedUrlPatterns.length > 0 || Object.keys(extraHeaders).length > 0 || Boolean(urlQuery),
  };
}
